import { useEffect, useMemo } from "react";
import { useDispatch } from "react-redux";
import {
  Navigate, Route, Routes, useLocation,
} from "react-router-dom";
import { useAuth } from "../modules/auth/useAuth";
import debounce from "lodash/debounce";
import About from "#/routes/About";
import Dashboard from "#/routes/Dashboard";
import Applications from "#/routes/Applications";
import ApplicationDetail from "#/routes/ApplicationDetail";
import Policies from "#/routes/Policies";
import Sandbox from "#/routes/Sandbox";
import Settings from "#/routes/Settings";
import Login from "#/routes/Login";
import Home from "#/routes/Home";
import Callback from "#/routes/Callback";
import Logout from "#/routes/Logout";
import NoAccess from "#/routes/NoAccess";
import Management from "#/routes/Management";
import { infraResize } from "#/infra/redux/actionCreator";
import Header from "#/modules/layout/Header";
import { ToastContainer } from "#/infra/Toastify";
import { useOrg } from "#/modules/auth/OrgContext";
import OrgSwitchModal from "#/modules/auth/OrgSwitchModal";
import OrgGateError from "#/modules/auth/OrgGateError";
import PageLoading from "#/modules/common/PageLoading";

const ProtectedRoute = ({ children }: { children: JSX.Element }) => {
  const auth = useAuth();
  // Entra: wait for GET /me to resolve the active Organization (X-License-Id) before rendering
  // (and thus before child pages fire their on-mount API calls). No-op for Cognito.
  const {
    gate, orgs, activeOrgId, defaultOrgId, switchOrg, setDefaultOrg, retry,
  } = useOrg();
  const expiresAt = auth.user?.expires_at;
  const nowSeconds = Math.floor(Date.now() / 1000);
  const isExpired = typeof expiresAt === "number" ? expiresAt <= nowSeconds : null;
  const shouldRedirect = !auth.isAuthenticated && !auth.isLoading;

  if (auth.isLoading) {
    return <PageLoading />;
  }

  if (isExpired || shouldRedirect) {
    return <Navigate to="/login" replace />;
  }

  // /me 403, or 200 with zero accessible orgs → contact-IT page.
  if (gate === "forbidden") {
    return <Navigate to="/no-access" replace />;
  }

  // /me failed transiently (5xx / network) → retry screen (not the permanent no-access page).
  if (gate === "error") {
    return <OrgGateError onRetry={retry} />;
  }

  // /me 200 but no default org and several to choose from → blocking Organization picker.
  // Rendered in place of children, so no protected page mounts (no API fires) until a pick.
  if (gate === "needs-org") {
    return (
      <OrgSwitchModal
        show
        forced
        orgs={orgs}
        activeOrgId={activeOrgId}
        defaultOrgId={defaultOrgId}
        onSwitch={switchOrg}
        onSetDefault={setDefaultOrg}
        onClose={() => undefined}
      />
    );
  }

  // Org / license still resolving via GET /me → project-standard loading indicator.
  if (gate === "pending") {
    return <PageLoading />;
  }

  return children;
};

const App = (): JSX.Element => {
  const dispatch = useDispatch();
  const location = useLocation();
  const pathname = useMemo(
    () => location.pathname.split("/"),
    [location.pathname],
  );
  const isLogin = pathname.includes("login");

  useEffect(() => {
    dispatch(infraResize());
    const debounceInfraResize = debounce(() => dispatch(infraResize()), 50);

    global.addEventListener("resize", debounceInfraResize);
    return function cleanup() {
      global.removeEventListener("resize", debounceInfraResize);
    };
  }, []);

  return (
    <>
      {!isLogin && <Header />}
      <ToastContainer />
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/about" element={<About />} />
        <Route
          path="/dashboard"
          element={(
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          )}
        />
        <Route
          path="/applications"
          element={(
            <ProtectedRoute>
              <Applications />
            </ProtectedRoute>
          )}
        />
        <Route
          path="/applications/:serviceTokenId"
          element={(
            <ProtectedRoute>
              <ApplicationDetail />
            </ProtectedRoute>
          )}
        />
        <Route
          path="/policies"
          element={(
            <ProtectedRoute>
              <Policies />
            </ProtectedRoute>
          )}
        />
        <Route
          path="/sandbox"
          element={(
            <ProtectedRoute>
              <Sandbox />
            </ProtectedRoute>
          )}
        />
        <Route
          path="/settings"
          element={(
            <ProtectedRoute>
              <Settings />
            </ProtectedRoute>
          )}
        />
        <Route
          path="/management"
          element={(
            <ProtectedRoute>
              <Management />
            </ProtectedRoute>
          )}
        />
        <Route path="/login" element={<Login />} />
        <Route path="/no-access" element={<NoAccess />} />
        <Route path="/callback" element={<Callback />} />
        <Route path="/logout" element={<Logout />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
};

export default App;
